import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactPlayer from "react-player/youtube";
import classes from "./VideoTestimonialsNew.module.css";
import useDeviceWidth from "../../../components/device-width/UseDeviceWidth";
import AdjustFontSize from "../../../components/adjust-font-size/AdjustFontSize";

const VideoTestimonialsNew = ({ data }) => {
  const width = useDeviceWidth();
  const [responsiveHtml, setResponsiveHtml] = useState("");
  const [videoReadyState, setVideoReadyState] = useState({});

  const getYoutubeIdFromURL = (url) => {
    const match = url.match(/\/shorts\/([^\?]+)/);
    return match ? match[1] : "";
  };

  const videoIds = useMemo(() => {
    return data?.youtubeShorts?.map(getYoutubeIdFromURL) || [];
  }, [data?.youtubeShorts]);

  const containerRefs = useRef({});
  const [visibleVideos, setVisibleVideos] = useState({});
  const [muteStates, setMuteStates] = useState({});

  useEffect(() => {
    if (data) {
      setResponsiveHtml(AdjustFontSize(data?.description, width));
    }
  }, [data, width]);

  useEffect(() => {
    const initialMuteStates = {};
    videoIds.forEach((id) => {
      initialMuteStates[id] = true;
    });
    setMuteStates(initialMuteStates);
  }, [videoIds]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleVideos((prev) => {
          const updated = { ...prev };
          const updatedReadyState = { ...videoReadyState };

          entries.forEach((entry) => {
            const videoId = entry.target.dataset.videoid;
            if (entry.isIntersecting) {
              updated[videoId] = true;
            } else {
              delete updated[videoId];
              updatedReadyState[videoId] = false; // reset ready state
            }
          });

          setVideoReadyState(updatedReadyState);
          return updated;
        });
      },
      { threshold: 0.5 }
    );

    const refs = Object.values(containerRefs.current);
    refs.forEach((el) => el && observer.observe(el));

    return () => {
      refs.forEach((el) => el && observer.unobserve(el));
    };
  }, [videoIds]);

  const toggleMute = (videoId) => {
    setMuteStates((prev) => ({
      ...prev,
      [videoId]: !prev[videoId],
    }));
  };

  const handlePlayerReady = (videoId) => {
    setVideoReadyState((prev) => ({
      ...prev,
      [videoId]: true,
    }));
  };

  const videoWidth = width > 768 ? 230 : 165;
  const videoHeight = width > 768 ? 409 : 293;

  return (
    <div className={classes.servicesContainer}>
      <img
        src={
          width > 1200
            ? "/img/shorts-back-desk.png"
            : width > 768
            ? "/img/shorts-back-tab.png"
            : "/img/shorts-back-mob.png"
        }
        alt="background"
        className={classes.pattern}
      />

      <div
        className={classes.headSection}
        data-aos="fade-down"
        data-aos-delay="150"
      >
        <h3 className={classes.ourServices}>{data?.subTitle}</h3>
        {data?.showTitle && (
          <h5
            className={classes.title}
            dangerouslySetInnerHTML={{ __html: data?.title }}
          />
        )}
      </div>

      <div className={classes.videoSection}>
        {videoIds.map((videoId, i) => (
          <div
            key={videoId}
            ref={(el) => (containerRefs.current[videoId] = el)}
            data-videoid={videoId}
            className={`${classes.videoDiv} ${
              width > 1200
                ? (i + 1) % 2 !== 0
                  ? classes.marginBottom
                  : classes.marginTop
                : (i + 1) % 2 !== 0
                ? classes.marginTop
                : classes.marginBottom
            }`}
            data-aos="fade-down"
            data-aos-delay="150"
            style={{
              position: "relative",
              width: videoWidth,
              height: videoHeight,
            }}
          >
            {visibleVideos[videoId] && (
              <div className={classes.videoWrapper}>
                {/* Thumbnail overlay until player is ready */}
                {!videoReadyState[videoId] && (
                  <img
                    className={classes.thumbnail}
                    src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                    alt="video thumbnail"
                    style={{
                      width: videoWidth,
                      height: videoHeight,
                      objectFit: "cover",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 1,
                      borderRadius: "30px",
                    }}
                  />
                )}
                <ReactPlayer
                  url={`https://www.youtube.com/watch?v=${videoId}`}
                  playing={true}
                  muted={muteStates[videoId]}
                  loop={true}
                  controls={false}
                  width={videoWidth}
                  height={videoHeight}
                  playsinline={true}
                  onReady={() => handlePlayerReady(videoId)}
                  style={{
                    opacity: videoReadyState[videoId] ? 1 : 0,
                    transition: "opacity 0.4s ease-in-out",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                  config={{
                    youtube: {
                      playerVars: {
                        modestbranding: 1,
                        rel: 0,
                        iv_load_policy: 3,
                        fs: 0,
                        playlist: videoId,
                      },
                    },
                  }}
                />
              </div>
            )}
            {!visibleVideos[videoId] && (
              <div className={classes.placeholder}>
                <img
                  src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                  alt="video thumbnail"
                  style={{
                    width: videoWidth,
                    height: videoHeight,
                    objectFit: "cover",
                  }}
                />
              </div>
            )}

            <button
              className={classes.muteButton}
              onClick={(e) => {
                e.stopPropagation();
                toggleMute(videoId);
              }}
              aria-label={muteStates[videoId] ? "Unmute" : "Mute"}
            >
              <img
                style={{ width: width > 1200 ? "18px" : "16px" }}
                src={
                  muteStates[videoId]
                    ? "/img/icons/speaker-mute.svg"
                    : "/img/icons/speaker-open.svg"
                }
                alt="mute toggle"
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoTestimonialsNew;
